import { detectRegion, detectStandaloneEnv, detectZone, Region, StandaloneEnv, zoneForRegion } from "config/backend";
import { isAppBuild } from "utils/buildType";

import { OTEL_COLLECTORS } from "./collectors";

// ── Library (SDK) configuration ──────────────────────────────────────────────

const SERVICE_NAME = "bim-webviewer-sdk";
const LIBRARY_NAME = "bim-webviewer-sdk";

export const SPAN_NAMES = {
    VIEWER_LOAD: "bim-webviewer-sdk.viewer.load",
    AUTH: "bim-webviewer-sdk.auth",
    ERROR_RECOVERY: "bim-webviewer-sdk.error.recovery",
    VIEWPOINT_LOADED: "bim-webviewer-sdk.viewpoint.loaded",
} as const;

// ── Standalone (app build) configuration ─────────────────────────────────────

const STANDALONE_SERVICE_NAME = "novoweb";
const STANDALONE_LIBRARY_NAME = "novoweb";

/**
 * Standalone OTEL trace collector endpoints (Novorender Azure infrastructure)
 */
const STANDALONE_OTEL_ENDPOINTS: Record<StandaloneEnv, string> = {
    [StandaloneEnv.TEST]: "https://otel-collector-web-test.azurewebsites.net/v1/traces",
    [StandaloneEnv.PRODUCTION]: "https://otel-collector-web-prod.azurewebsites.net/v1/traces",
    [StandaloneEnv.LOCAL]: "http://localhost:4318/v1/traces",
} as const;

/**
 * Standalone OTEL metrics collector endpoints (Novorender Azure infrastructure)
 */
const STANDALONE_OTEL_METRICS_ENDPOINTS: Record<StandaloneEnv, string> = {
    [StandaloneEnv.TEST]: "https://otel-collector-web-test.azurewebsites.net/v1/metrics",
    [StandaloneEnv.PRODUCTION]: "https://otel-collector-web-prod.azurewebsites.net/v1/metrics",
    [StandaloneEnv.LOCAL]: "http://localhost:4318/v1/metrics",
} as const;

const STANDALONE_SPAN_NAMES = {
    VIEWER_LOAD: "novoweb.viewer.load",
    AUTH: "novoweb.auth",
    ERROR_RECOVERY: "novoweb.error.recovery",
    VIEWPOINT_LOADED: "novoweb.viewpoint.loaded",
} as const;

// ── Resolved config ──────────────────────────────────────────────────────────

export type SpanNameMap = typeof SPAN_NAMES | typeof STANDALONE_SPAN_NAMES;

export interface ResolvedTelemetryConfig {
    endpoint: string;
    /** Metrics are not exported when absent. */
    metricsEndpoint?: string;
    serviceName: string;
    libraryName: string;
    region: Region | StandaloneEnv;
    zone: string;
    version: string;
    spanNames: SpanNameMap;
}

function getDevelopmentRegionOverride(): Region | undefined {
    const override = import.meta.env.DEV ? import.meta.env.REACT_APP_TELEMETRY_REGION : undefined;
    if (!override || !Object.values(Region).includes(override as Region)) {
        return undefined;
    }

    return override as Region;
}

const TELEMETRY_ZONES = ["us01", "us02", "uk01", "eu01", "qa", "local"] as const;

function getDevelopmentZoneOverride(): string | undefined {
    const override = import.meta.env.DEV ? import.meta.env.REACT_APP_TELEMETRY_ZONE : undefined;
    if (!override || !TELEMETRY_ZONES.includes(override)) {
        return undefined;
    }

    return override;
}

/**
 * Resolve telemetry configuration based on build type and environment.
 * Returns `null` when telemetry should be disabled (localhost, unless a
 * development-only region override is explicitly set; app builds honor only `local`).
 */
export function resolveTelemetryConfig(): ResolvedTelemetryConfig | null {
    const isStandalone = isAppBuild();

    if (isStandalone) {
        const env = detectStandaloneEnv();

        const isLocalCollector = env === StandaloneEnv.LOCAL && getDevelopmentRegionOverride() === Region.LOCAL;
        if (env === StandaloneEnv.LOCAL && !isLocalCollector) {
            return null;
        }

        return {
            // Proxied to Jaeger by the dev server, which has no metrics pipeline.
            endpoint: isLocalCollector ? "/otel/v1/traces" : STANDALONE_OTEL_ENDPOINTS[env],
            metricsEndpoint: isLocalCollector ? undefined : STANDALONE_OTEL_METRICS_ENDPOINTS[env],
            serviceName: STANDALONE_SERVICE_NAME,
            libraryName: STANDALONE_LIBRARY_NAME,
            region: env,
            zone: env,
            version: __APP_VERSION__,
            spanNames: STANDALONE_SPAN_NAMES,
        };
    }

    const developmentRegionOverride = getDevelopmentRegionOverride();
    const region = developmentRegionOverride ?? detectRegion();

    let endpoint: string;
    let metricsEndpoint: string | undefined;
    if (developmentRegionOverride) {
        // The dev server proxies `/otel` to the override's collector (see vite.config.ts).
        endpoint = "/otel/v1/traces";
        // Jaeger (docker-compose.telemetry.yml) has no metrics pipeline.
        metricsEndpoint = region === Region.LOCAL ? undefined : "/otel/v1/metrics";
    } else if (region === Region.LOCAL) {
        return null;
    } else {
        endpoint = `${OTEL_COLLECTORS[region]}/v1/traces`;
        metricsEndpoint = `${OTEL_COLLECTORS[region]}/v1/metrics`;
    }

    return {
        endpoint,
        metricsEndpoint,
        serviceName: SERVICE_NAME,
        libraryName: LIBRARY_NAME,
        region,
        zone:
            getDevelopmentZoneOverride() ??
            (developmentRegionOverride ? zoneForRegion(developmentRegionOverride) : detectZone()),
        version: __LIB_VERSION__,
        spanNames: SPAN_NAMES,
    };
}
